import gsap from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { CustomEase } from 'gsap/CustomEase';
import { qsa } from './utils';

gsap.registerPlugin(ScrollTrigger, CustomEase);

CustomEase.create('heroEase', '0.16, 1, 0.3, 1');

export function initHeroAnimations() {
  const heroSection = document.querySelector('.hero-section');
  if (!heroSection) return;

  if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
    return;
  }

  const bgImage = heroSection.querySelector('.hero-section__image');
  const heading = heroSection.querySelector('.hero-section__heading');
  const description = heroSection.querySelector('.hero-section__description');
  const cta = heroSection.querySelector('.hero-section__cta');

  const tl = gsap.timeline();

  if (bgImage) {
    gsap.from(bgImage, {
      scale: 1.05,
      duration: 3,
      ease: 'heroEase',
      clearProps: 'transform'
    });
  }

  if (heading) {
    tl.from(heading, {
      opacity: 0,
      y: 20,
      duration: 0.9,
      ease: 'heroEase'
    }, 0.2);
  }

  if (description) {
    tl.from(description, {
      opacity: 0,
      y: 20,
      duration: 0.9,
      ease: 'heroEase'
    }, 0.45);
  }

  if (cta) {
    tl.from(cta, {
      opacity: 0,
      y: 20,
      duration: 0.9,
      ease: 'heroEase'
    }, 0.7);
  }
}

export function initRevealAnimations() {
  const elements = qsa('[data-reveal]');
  if (!elements.length) return;

  if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
    return;
  }

  elements.forEach((el) => {
    gsap.from(el, {
      opacity: 0,
      y: 40,
      duration: 0.8,
      ease: 'power2.out',
      scrollTrigger: {
        trigger: el,
        start: 'top 60%',
        toggleActions: 'play none none none',
      },
    });
  });
}

export function initMediaTextAnimations() {
  const sections = qsa('.media-text-section');
  if (!sections.length) return;

  if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
    return;
  }

  sections.forEach((section) => {
    const isReverse = section.querySelector('.media-text--reverse') !== null;
    const heading = section.querySelector('.media-text__heading');
    // Animate the image wrapper or the image itself? The image itself works fine, but let's animate the wrapper if we want.
    // The wrapper has overflow hidden usually? Let's stick to the image wrapper to be safe, or the image. I'll use the image.
    const image = section.querySelector('.media-text__media'); 
    const body = section.querySelector('.media-text__body');
    const cta = section.querySelector('.media-text__cta');
    const bottomElements = [];
    if (body) bottomElements.push(body);
    if (cta) bottomElements.push(cta);

    const tl = gsap.timeline({
      scrollTrigger: {
        trigger: section,
        start: 'top 60%',
      }
    });

    if (isReverse) {
      if (heading) {
        tl.from(heading, { opacity: 0, x: -24, duration: 1.2, ease: 'heroEase' }, 0.1);
      }
      if (image) {
        tl.from(image, { opacity: 0, x: 24, scale: 0.98, duration: 1.2, ease: 'heroEase', clearProps: 'transform' }, 0.3);
      }
      if (bottomElements.length) {
        tl.from(bottomElements, { opacity: 0, y: 16, duration: 1.2, ease: 'heroEase' }, 0.5);
      }
    } else {
      if (image) {
        tl.from(image, { opacity: 0, x: -24, scale: 0.98, duration: 1.2, ease: 'heroEase', clearProps: 'transform' }, 0.1);
      }
      if (heading) {
        tl.from(heading, { opacity: 0, x: 24, duration: 1.2, ease: 'heroEase' }, 0.3);
      }
      if (bottomElements.length) {
        tl.from(bottomElements, { opacity: 0, y: 16, duration: 1.2, ease: 'heroEase' }, 0.5);
      }
    }
  });
}

// The decorative waves fade in as a single layer — all strokes together, a beat
// after the section arrives — and then hold. Figma's motion frame loops this
// forever and drifts each stroke on its own offset; both were dropped because
// together they read as the lines redrawing themselves.
const WAVES_DELAY = 1;
const WAVES_FADE_IN = 1.5;

function initCardsWaves(section) {
  const waves = section.querySelector('.cards-section__waves');
  if (!waves) return;

  const scrollTrigger = {
    trigger: section,
    start: 'top 60%',
    end: 'bottom top',
    // Fade once on the way in and then hold. Rewinding when the section leaves
    // is what lets it play again if the user scrolls back to it.
    toggleActions: 'restart none restart reset',
  };

  // The delay is a gap in a timeline rather than a tween `delay` so that the
  // `restart` toggle action replays it too.
  gsap.set(waves, { opacity: 0 });

  gsap.timeline({ scrollTrigger }).to(waves, {
    opacity: 1,
    duration: WAVES_FADE_IN,
    // Not `heroEase` here: its overshoot curve reaches 87% opacity in the first
    // quarter of the tween, so the fade itself never reads.
    ease: 'power2.out',
  }, WAVES_DELAY);

  // Per-stroke variant — each line in `waves.svg` fading on its own offset,
  // bottom-most first. Rejected at design review in favour of the whole layer
  // appearing at once; kept here so it can be put back without re-deriving it.
  // gsap.from(qsa('.cards-section__waves path', section), {
  //   opacity: 0,
  //   duration: 1.6,
  //   stagger: 0.4,
  //   ease: 'power2.out',
  //   scrollTrigger,
  // });
}

export function initCardsAnimations() {
  const sections = qsa('.cards-section');
  if (!sections.length) return;

  if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
    return;
  }

  sections.forEach((section) => {
    const content = section.querySelector('.cards-section__content');
    const cards = section.querySelector('.cards-section__cards');

    initCardsWaves(section);

    const tl = gsap.timeline({
      scrollTrigger: {
        trigger: section,
        start: 'top 60%',
      }
    });

    if (content) {
      tl.from(content, { opacity: 0, duration: 1.1, ease: 'power2.out' }, 0.3);
    }
    
    if (cards) {
      tl.from(cards, { opacity: 0, y: 20, duration: 1.1, ease: 'heroEase' }, 0.6);
    }
  });
}

export function initGetInTouchAnimations() {
  const sections = qsa('.get-in-touch-section');
  if (!sections.length) return;

  if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
    return;
  }

  sections.forEach((section) => {
    const content = section.querySelector('.get-in-touch-section__content');
    const info = section.querySelector('.get-in-touch-section__info');

    const tl = gsap.timeline({
      scrollTrigger: {
        trigger: section,
        start: 'top 60%',
      }
    });

    if (content) {
      tl.from(content, { opacity: 0, y: 40, duration: 1.5, ease: 'heroEase' }, 0);
    }

    if (info) {
      tl.from(info, { opacity: 0, x: 60, duration: 1.5, ease: 'heroEase' }, 0.15);
    }
  });
}
